export enum ExerciseSortingCriteria {
  ALPHABET = 'ALPHABET',
  DIFFICULTY = 'DIFFICULTY',
}

export enum ExerciseSortingDirection {
  LOW_TO_HIGH = 'LOW_TO_HIGH',
  HIGH_TO_LOW = 'HIGH_TO_LOW',
}

export class ExerciseSorting {
  criteria: ExerciseSortingCriteria = ExerciseSortingCriteria.ALPHABET;
  direction: ExerciseSortingDirection = ExerciseSortingDirection.LOW_TO_HIGH;
}

/**
 * The grid's sorting is a per-device display preference, not office data, so it
 * lives in localStorage — the same treatment the side list's sorting gets in
 * `template-sorting.ts`.
 */
const EXERCISE_SORTING_STORAGE_KEY = 'exerciseLibrary_exerciseSorting';

const isCriteria = (value: unknown): value is ExerciseSortingCriteria =>
  Object.values(ExerciseSortingCriteria).includes(value as ExerciseSortingCriteria);

const isDirection = (value: unknown): value is ExerciseSortingDirection =>
  Object.values(ExerciseSortingDirection).includes(value as ExerciseSortingDirection);

/**
 * A stored value that is unparseable or names an option that no longer exists
 * falls back to a fresh `ExerciseSorting`, so neither a hand-edited key nor a
 * deploy that renames a criteria can throw while the store is being created.
 */
export const loadExerciseSorting = (): ExerciseSorting => {
  const sorting = new ExerciseSorting();

  try {
    const raw = localStorage.getItem(EXERCISE_SORTING_STORAGE_KEY);
    if (!raw) return sorting;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return sorting;

    const { criteria, direction } = parsed as Partial<ExerciseSorting>;
    if (!isCriteria(criteria) || !isDirection(direction)) return sorting;

    sorting.criteria = criteria;
    sorting.direction = direction;
    return sorting;
  } catch {
    return sorting;
  }
};

export const saveExerciseSorting = (sorting: ExerciseSorting): void => {
  try {
    localStorage.setItem(
      EXERCISE_SORTING_STORAGE_KEY,
      JSON.stringify({ criteria: sorting.criteria, direction: sorting.direction }),
    );
  } catch {
    // A full or blocked storage (Safari private mode) costs the preference for
    // the next session, nothing more — never the interaction that changed it.
  }
};
